import {
  parse,
  parseISO,
  format,
  intervalToDuration,
  differenceInCalendarDays,
  differenceInDays,
  differenceInMonths,
  set,
  isBefore,
  addYears
} from 'date-fns';
import * as dateFnsTz from 'date-fns-tz';
import { USAStatesByAbbv } from '@ourbranch/utility-types';
import { stateToTzMap } from '@ourbranch/state-to-tz-map';

// Re-export date-fns and date-fns-tz to consumers
import * as dateFns from 'date-fns';

const specificDriverAgeLogicStates = ['AL', 'LA', 'ME', 'SC', 'SD', 'VT'];

export function getDateFromString(date: string, customFormat?: string | undefined) {
  if (customFormat) {
    return parse(date, customFormat, new Date());
  }

  if (String(date).length === 8) {
    return parse(String(date), 'yyyyMMdd', new Date());
  }

  return parse(date, 'yyyy-MM-dd', new Date());
}

export function getStringFromDate(date: Date, customFormat?: string | undefined) {
  if (customFormat) {
    return format(date, customFormat);
  }
  return format(date, 'yyyy-MM-dd');
}

// Given a date with this format: 2020-01-01 it will return the numeric representation 20200101
export function getNumberFromString(date: string) {
  return parse(date, 'yyyyMMdd', new Date());
}

export function getIntervalBetween(start: Date, end: Date) {
  return intervalToDuration({
    start,
    end
  });
}

export function getDaysBetweenDates(start: Date, end: Date) {
  return differenceInCalendarDays(end, start);
}

export function getSecondsBetweenDates(start: Date, end: Date) {
  return dateFns.differenceInSeconds(end, start);
}

export function getMonthsBetweenDates(start: Date, end: Date) {
  return differenceInMonths(end, start);
}

// We default to Ohio for our state for timezone purposes
let tzSavedState = 'OH';

/** Returns an object describing age given a Date object set to birthday. */
export function calculateAge(birthday: Date, startMoment = new Date()): { years: number; months: number } {
  const lifetime = getIntervalBetween(birthday, startMoment);
  return { years: lifetime.years!, months: lifetime.months! };
}

/** Returns number of days between passed date and birthdate */
export function daysBetweenDateAndNextBirthday(date: Date, birthDate: Date) {
  const thisYearBirthday = set(birthDate, { year: date.getFullYear() });

  if (isBefore(date, thisYearBirthday)) {
    return getDaysBetweenDates(date, thisYearBirthday);
  }

  return getDaysBetweenDates(date, addYears(thisYearBirthday, 1));
}

/** Returns an object describing Driver age given a Date object set to birthday and an effective date. */
export function calculatePersonAge(
  birthday: Date,
  effectiveDate: Date | null,
  startMoment: Date = new Date(),
  stateIn?: USAStatesByAbbv
): { years: number; months: number } {
  const state = stateIn || tzSavedState; // we recall the last state if one isn't sent in
  tzSavedState = state;

  const lifetime = getIntervalBetween(birthday, startMoment);
  const lifetimeYears = lifetime.years as number;
  const daysBetweenEffectiveDateAndNextBirthday = effectiveDate
    ? daysBetweenDateAndNextBirthday(effectiveDate, birthday)
    : null;

  // For specificDriverAgeLogicStates, if the driver (is 24) OR (younger than 24 AND has a birthday within 30 days of the effective date)
  if (
    specificDriverAgeLogicStates.includes(state) &&
    (lifetimeYears === 24 ||
      (lifetimeYears < 24 && daysBetweenEffectiveDateAndNextBirthday && daysBetweenEffectiveDateAndNextBirthday <= 30))
  ) {
    return { years: lifetime.years! + 1, months: 0 };
  }
  return calculateAge(birthday, startMoment);
}

/** Returns YYYY-MM-DD from YYYYMMDD */
export function yyyymmddToAWSDate(yyyymmddStrIn: string) {
  const yyyymmddStr = typeof yyyymmddStrIn === 'string' ? yyyymmddStrIn : String(yyyymmddStrIn);
  return yyyymmddStr.length === 8
    ? [yyyymmddStr.slice(0, 4), yyyymmddStr.slice(4, 6), yyyymmddStr.slice(6, 8)].join('-')
    : null;
}

/** Helper function that pads a string to two digits */
function padTwoDigits(str: string) {
  return String(`00${String(str)}`).slice(-2);
}

/** Returns YYYY-MM-DD from an Excel Date  */
export function ExcelDateToAWSDate(serial: number) {
  const utcDays = Math.floor(serial - 25569);
  const utcValue = utcDays * 86400;
  const dateInfo = new Date(utcValue * 1000);

  return `${dateInfo.getFullYear()}-${padTwoDigits(String(dateInfo.getMonth()))}-${padTwoDigits(
    String(dateInfo.getDate())
  )}`;
}

/** take a variety of different date formats and turn them all into YYYY-MM-DD */
export function normalizeDate(inputDate: string) {
  if (inputDate.length === 8 && !inputDate.includes('-')) {
    // assuming YYYYMMDD date
    return yyyymmddToAWSDate(inputDate);
  }
  if (inputDate.length < 8 && parseInt(inputDate, 10) > 0) {
    // assuming excel date:
    return ExcelDateToAWSDate(parseInt(inputDate, 10));
  }

  // return what we received
  return inputDate;
}

/**
 * Rate Control Date (RCD) staleness helpers — issue #32130.
 *
 * RCD (offer.quote.global.rateControlDate, a 'YYYY-MM-DD' string in the policy
 * state's timezone) is the rater's "as-of" date: it selects the ClarionDoor rate
 * filing and feeds surplus-contribution / roof-age / credit-algo / violation-rule
 * choices. Historically a policy could only bind when RCD === today, which forced a
 * re-rate at bind. We now allow "holding" the RCD (and therefore the rate) for a
 * brand- and role-configurable number of days. These pure functions are the single
 * source of truth for "how old is the RCD" / "is it older than N days", shared by the
 * bind gate (charge-policy-step), the rater (OfferRaterManager), and staff-fe so all
 * three agree on the semantics.
 *
 * Compare against a `todayStr` produced by getTodayStr(state) so the comparison happens
 * in the policy's timezone (both sides are 'YYYY-MM-DD' calendar dates).
 */

/**
 * Whole calendar days the RCD is behind `todayStr`. 0 when they are the same day, and
 * clamped to 0 for a future RCD (never negative). Accepts 'YYYY-MM-DD' or 'YYYYMMDD'.
 */
export function rateControlDateDaysOld(rateControlDate: string, todayStr: string): number {
  const daysOld = getDaysBetweenDates(getDateFromString(rateControlDate), getDateFromString(todayStr));
  return daysOld > 0 ? daysOld : 0;
}

/**
 * True when the RCD is strictly MORE than `maxDaysOld` days old — i.e. it fails the bind
 * gate and the rater must refresh it. `maxDaysOld = 0` reproduces the legacy "must be
 * today" rule (anything earlier than today is stale). A missing RCD or `todayStr` is
 * treated as stale/invalid so the bind gate keeps rejecting bad data.
 */
export function isRateControlDateOlderThan({
  rateControlDate,
  todayStr,
  maxDaysOld
}: {
  rateControlDate: string | null | undefined;
  todayStr: string | null | undefined;
  maxDaysOld: number;
}): boolean {
  if (!rateControlDate || !todayStr) {
    return true;
  }
  return rateControlDateDaysOld(rateControlDate, todayStr) > maxDaysOld;
}

/** Return today's date as a string, accounting for timezones. */
export function getTodayStr(stateIn: string | undefined | null) {
  const state = stateIn || tzSavedState; // we recall the last state if one isn't sent in
  tzSavedState = state;

  if (['WA', 'CA', 'OR', 'NV'].includes(state)) {
    return dateFnsTz.formatInTimeZone(new Date(), 'America/Los_Angeles', 'yyyy-MM-dd');
  }
  if (['AZ', 'NM', 'WY', 'UT', 'CO', 'MT', 'ID', 'ND', 'SD', 'NE'].includes(state)) {
    return dateFnsTz.formatInTimeZone(new Date(), 'America/Boise', 'yyyy-MM-dd');
  }
  if (['AL', 'AR', 'IL', 'IA', 'LA', 'MN', 'MO', 'MS', 'OK', 'WI', 'IN', 'KS', 'MI', 'TN', 'TX'].includes(state)) {
    return dateFnsTz.formatInTimeZone(new Date(), 'America/Chicago', 'yyyy-MM-dd');
  }

  if (state === 'HI') {
    return dateFnsTz.formatInTimeZone(new Date(), 'Pacific/Honolulu', 'yyyy-MM-dd');
  }
  if (state === 'AK') {
    return dateFnsTz.formatInTimeZone(new Date(), 'America/Anchorage', 'yyyy-MM-dd');
  }
  // default to eastern
  return dateFnsTz.formatInTimeZone(new Date(), 'America/New_York', 'yyyy-MM-dd');
}

/** Return date as a string, accounting for timezones. */
export function getDateStrInPolicyTZ(state: string, dateStr: string) {
  // force end of day in utc otherwise will be a day off
  const date = new Date(dateStr?.includes('T') ? dateStr : `${dateStr}T23:59:59.999Z`);

  if (['WA', 'CA', 'OR', 'NV'].includes(state)) {
    return dateFnsTz.formatInTimeZone(date, 'America/Los_Angeles', 'yyyy-MM-dd');
  }
  if (['AZ', 'NM', 'WY', 'UT', 'CO', 'MT', 'ID', 'ND', 'SD', 'NE'].includes(state)) {
    return dateFnsTz.formatInTimeZone(date, 'America/Boise', 'yyyy-MM-dd');
  }
  if (['AL', 'AR', 'IL', 'IA', 'LA', 'MN', 'MO', 'MS', 'OK', 'WI', 'IN', 'KS', 'MI', 'TN', 'TX'].includes(state)) {
    return dateFnsTz.formatInTimeZone(date, 'America/Chicago', 'yyyy-MM-dd');
  }

  if (state === 'HI') {
    return dateFnsTz.formatInTimeZone(date, 'Pacific/Honolulu', 'yyyy-MM-dd');
  }
  if (state === 'AK') {
    return dateFnsTz.formatInTimeZone(date, 'America/Anchorage', 'yyyy-MM-dd');
  }
  // default to eastern
  return dateFnsTz.formatInTimeZone(date, 'America/New_York', 'yyyy-MM-dd');
}

// ============================================================================
// Absorbed from the retired @ourbranch/date-helpers (#32561). Behavior is
// preserved byte-for-byte from that package so the consolidation is a no-op.
// ============================================================================

/** Coerce a date-ish value to a Date. Strings are parsed as ISO-8601 (local
 * midnight for date-only, matching the previous moment() behavior); Date/number
 * go through the Date constructor. A nullish value yields an Invalid Date (NaN)
 * rather than silently resolving to the Unix epoch — matching moment(null). */
const toDateValue = (value: Date | string | number): Date => {
  if (value == null) {
    return new Date(NaN);
  }
  return typeof value === 'string' ? parseISO(value) : new Date(value);
};

/** Truncate toward zero (matches moment's absFloor used by moment.duration). */
const absFloor = (n: number): number => (n < 0 ? Math.ceil(n) : Math.floor(n));

/**
 * Age using moment.duration()'s 365.25-day-average year semantics (days → months
 * via 4800/146097, months → years via /12). Distinct from {@link calculateAge},
 * which is calendar-accurate: the two disagree near birthdays (e.g. an exact 30
 * calendar years reads as 29y 11m here). Rating/underwriting relies on this
 * approximation, so consumers of the old date-helpers.calculateAge map to THIS.
 */
export function calculateAgeFromDuration(
  birthday: Date | string,
  startMoment: Date | string | number = new Date()
): { years: number; months: number } {
  const days = absFloor((toDateValue(startMoment).getTime() - toDateValue(birthday).getTime()) / 86_400_000);
  const monthsTotal = absFloor((days * 4800) / 146097);
  const years = absFloor(monthsTotal / 12);
  return { years, months: monthsTotal - years * 12 };
}

/** Returns a boolean depending upon whether the supplied string is in the future */
export function isFutureDate(yyyymmddStr: string) {
  return parseInt(yyyymmddStr.replace(/[^0-9]/g, ''), 10) >= parseInt(format(new Date(), 'yyyyMMdd'), 10);
}

/** Returns the number of days between now and a supplied day.
 * `day` must be an ISO-8601 string — `parseISO` is strict and returns NaN for
 * other formats (unlike the previous lenient moment(day) parsing). */
export function daysBetween(day: string) {
  return differenceInDays(new Date(), parseISO(day)) - 1;
}

/**
 * Parse a date string that may be ISO ('yyyy-MM-dd' or a full ISO datetime) or US 'MM/dd/yyyy'
 * (e.g. vendor inspection dates). Returns a Date, or null when the value is missing/unparseable.
 * Timezone-naive — intended for date-only business values. (#33158)
 */
export function parseFlexibleDate(value?: string | null): Date | null {
  if (!value || typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();

  // ISO: 'yyyy-MM-dd' and full ISO datetimes.
  const iso = parseISO(trimmed);
  if (!Number.isNaN(iso.getTime())) {
    return iso;
  }

  // US-format 'MM/dd/yyyy'. Fixed reference date keeps parsing deterministic (no dependency on now).
  const us = parse(trimmed, 'MM/dd/yyyy', new Date(0));
  if (!Number.isNaN(us.getTime())) {
    return us;
  }

  return null;
}

/**
 * Return the latest of several flexible-format date strings (see {@link parseFlexibleDate}) as a
 * Date, or null when none parse. Callers format the result for their own needs. (#33158)
 */
export function pickLatestFlexibleDate(...values: Array<string | null | undefined>): Date | null {
  const dates = values.map(parseFlexibleDate).filter((d): d is Date => d !== null);
  if (!dates.length) {
    return null;
  }
  return dates.reduce((a, b) => (a.getTime() >= b.getTime() ? a : b));
}

export const localToUtcTime = (date: string | number | Date, state: string) => {
  return dateFnsTz.zonedTimeToUtc(date, stateToTzMap[state]);
};

export { dateFns, dateFnsTz };
